import React, { useState, useEffect } from 'react';
import { useNavigation, SmartBackButton, SmartNextButton, NavigationFeedback } from './NavigationManager';
import { Home, Search, Users, Settings, Bell, HelpCircle, Wifi, WifiOff } from 'lucide-react';

/**
 * SCREEN-SPECIFIC NAVIGATION COMPONENTS
 * ====================================
 * 
 * Specialized navigation components for different screens in Likico
 * with preview mode support and accessibility compliance
 */

// Screen Navigator for Group Search
export const GroupSearchNavigator = ({ 
  onBack, 
  onSearch, 
  searchQuery = '',
  hasResults = false,
  isLoading = false 
}) => {
  const { t, simulationMode, showFeedback } = useNavigation();
  
  const handleBack = () => {
    if (onBack) {
      onBack();
    } else if (simulationMode) {
      showFeedback('info', `${t.simulatedNavigation} écran principal`);
    }
  };
  
  const handleSearch = () => {
    if (isLoading) return;
    
    if (onSearch) {
      onSearch(searchQuery);
    } else if (simulationMode) {
      showFeedback('success', `${t.simulatedNavigation} résultats de recherche pour "${searchQuery}"`);
    }
  };
  
  return (
    <div className="bg-white border-b border-gray-200 px-4 py-3 sticky top-0 z-40">
      <div className="flex items-center justify-between max-w-4xl mx-auto">
        <SmartBackButton 
          onCustomBack={handleBack}
          variant="minimal"
          className="flex-shrink-0"
        />
        
        <div className="flex-1 mx-4 flex items-center gap-3">
          <Search className="h-5 w-5 text-gray-400" />
          <h1 className="text-lg font-semibold text-gray-900">Rechercher des groupes</h1>
          
          {simulationMode && (
            <div className="flex items-center gap-1 px-2 py-1 bg-orange-100 rounded-full text-xs text-orange-800">
              <WifiOff className="h-3 w-3" />
              Preview
            </div>
          )}
        </div>
        
        {searchQuery.trim() && (
          <SmartNextButton
            onCustomNext={handleSearch}
            label="Rechercher"
            variant="primary"
            isLoading={isLoading}
            className="flex-shrink-0"
          />
        )}
      </div>
    </div>
  );
};

// Screen Navigator for Group Creation Flow
export const GroupCreationNavigator = ({ 
  currentStep = 0,
  totalSteps = 4,
  onBack,
  onNext,
  onCancel,
  isValid = true,
  isLoading = false,
  stepData = {}
}) => {
  const { t, simulationMode, showFeedback } = useNavigation();
  
  const steps = [
    { key: 'basic', label: 'Informations' },
    { key: 'settings', label: 'Paramètres' },
    { key: 'visibility', label: 'Visibilité' },
    { key: 'confirmation', label: 'Confirmation' }
  ];
  
  const currentStepInfo = steps[currentStep];
  const isLastStep = currentStep === totalSteps - 1;
  
  const handleBack = () => {
    if (currentStep === 0 && onCancel) {
      onCancel();
    } else if (onBack) {
      onBack();
    } else if (simulationMode) {
      showFeedback('success', `${t.simulatedNavigation} étape précédente. ${t.dataPreserved}.`);
    }
  };
  
  const handleNext = () => {
    if (onNext) {
      onNext(stepData);
    } else if (simulationMode) {
      const nextLabel = isLastStep ? 'création du groupe' : 'étape suivante';
      showFeedback('success', `${t.simulatedNavigation} ${nextLabel}. ${t.dataPreserved}.`);
    }
  };
  
  return (
    <>
      {/* Header with progress */}
      <div className="bg-white border-b border-gray-200 px-4 py-4 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto">
          {/* Title and cancel */}
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Users className="h-6 w-6 text-green-600" />
              Créer un groupe
            </h1>
            
            <div className="flex items-center gap-2">
              {simulationMode && (
                <div className="flex items-center gap-1 px-2 py-1 bg-orange-100 rounded-full text-xs text-orange-800">
                  <WifiOff className="h-3 w-3" />
                  Preview
                </div>
              )}
              
              <button
                onClick={onCancel}
                className="text-gray-500 hover:text-gray-700 p-1 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                aria-label="Annuler la création"
              >
                <span className="text-sm">Annuler</span>
              </button>
            </div>
          </div>
          
          {/* Progress indicator */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm text-gray-600">
              <span>Étape {currentStep + 1} sur {totalSteps}</span>
              <span>{currentStepInfo?.label}</span>
            </div>
            
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-green-600 h-2 rounded-full transition-all duration-500"
                style={{ width: `${((currentStep + 1) / totalSteps) * 100}%` }}
                role="progressbar"
                aria-valuenow={(currentStep + 1) / totalSteps * 100}
                aria-valuemin="0"
                aria-valuemax="100"
                aria-label={`Progression de création: ${Math.round(((currentStep + 1) / totalSteps) * 100)}%`}
              />
            </div>
            
            {/* Step indicators */}
            <div className="flex justify-between">
              {steps.map((step, index) => (
                <div key={step.key} className="flex flex-col items-center">
                  <div className={`w-3 h-3 rounded-full ${
                    index <= currentStep ? 'bg-green-600' : 'bg-gray-300'
                  }`} />
                  <span className="text-xs text-gray-500 mt-1">{step.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      
      {/* Bottom navigation */}
      <div className="bg-white border-t border-gray-200 px-4 py-3 sticky bottom-0 z-40">
        <div className="flex items-center justify-between max-w-4xl mx-auto">
          <SmartBackButton 
            onCustomBack={handleBack}
            preserveData={true}
            disabled={isLoading}
          />
          
          <SmartNextButton
            onCustomNext={handleNext}
            label={isLastStep ? 'Créer le groupe' : t.next}
            variant={isLastStep ? 'success' : 'primary'}
            disabled={!isValid}
            isLoading={isLoading}
            newData={stepData}
          />
        </div>
      </div>
    </>
  );
};

// Screen Navigator for Join Request Flow  
export const JoinRequestNavigator = ({
  currentStep = 0,
  onBack,
  onNext,
  onCancel,
  groupInfo = null,
  isLoading = false
}) => {
  const { t, simulationMode, showFeedback } = useNavigation();
  
  const steps = ['Recherche', 'Sélection', 'Demande', 'Confirmation'];
  const currentStepLabel = steps[currentStep];
  
  const handleBack = () => {
    if (currentStep === 0 && onCancel) {
      onCancel();
    } else if (onBack) {
      onBack();
    } else if (simulationMode) {
      showFeedback('success', `${t.simulatedNavigation} étape précédente`);
    }
  };
  
  const handleNext = () => {
    if (onNext) {
      onNext();
    } else if (simulationMode) {
      showFeedback('success', `${t.simulatedNavigation} ${steps[currentStep + 1] || 'finalisation'}`);
    }
  };
  
  return (
    <>
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 sticky top-0 z-40">
        <div className="flex items-center justify-between max-w-4xl mx-auto">
          <div className="flex items-center gap-3">
            <SmartBackButton 
              onCustomBack={handleBack}
              variant="minimal"
              disabled={isLoading}
            />
            
            <div>
              <h1 className="text-lg font-semibold text-gray-900">Rejoindre un groupe</h1>
              <div className="text-sm text-gray-500">{currentStepLabel}</div>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {simulationMode && (
              <div className="flex items-center gap-1 px-2 py-1 bg-orange-100 rounded-full text-xs text-orange-800">
                <WifiOff className="h-3 w-3" />
                Preview
              </div>
            )}
            
            {groupInfo && (
              <div className="text-sm text-gray-600">
                {groupInfo.name}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

// Universal Screen Header with navigation
export const ScreenHeader = ({
  title,
  subtitle,
  icon: Icon,
  onBack,
  actions = [],
  showPreviewIndicator = true
}) => {
  const { simulationMode, showFeedback, t } = useNavigation();
  
  const handleBack = () => {
    if (onBack) {
      onBack();
    } else if (simulationMode) {
      showFeedback('info', `${t.simulatedNavigation} écran précédent`);
    }
  };
  
  return (
    <div className="bg-white border-b border-gray-200 px-4 py-3 sticky top-0 z-40">
      <div className="flex items-center justify-between max-w-4xl mx-auto">
        {/* Left side - Back button and title */}
        <div className="flex items-center gap-3 flex-1">
          <SmartBackButton 
            onCustomBack={handleBack}
            variant="minimal"
            className="flex-shrink-0"
          />
          
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {Icon && <Icon className="h-6 w-6 text-blue-600 flex-shrink-0" />}
            <div className="min-w-0 flex-1">
              <h1 className="text-lg font-semibold text-gray-900 truncate">{title}</h1>
              {subtitle && <div className="text-sm text-gray-500 truncate">{subtitle}</div>}
            </div>
          </div>
        </div>
        
        {/* Right side - Actions and indicators */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {showPreviewIndicator && simulationMode && (
            <div className="flex items-center gap-1 px-2 py-1 bg-orange-100 rounded-full text-xs text-orange-800">
              <WifiOff className="h-3 w-3" />
              Preview
            </div>
          )}
          
          {actions.map((action, index) => (
            <button
              key={index}
              onClick={action.onClick}
              disabled={action.disabled}
              className={`p-2 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                action.disabled 
                  ? 'text-gray-400 cursor-not-allowed' 
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
              aria-label={action.label}
              title={action.label}
            >
              <action.icon className="h-5 w-5" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// Modal Navigation (for dialogs and overlays)
export const ModalNavigation = ({
  title,
  onClose,
  onBack,
  onNext,
  nextLabel,
  nextDisabled = false,
  isLoading = false,
  showProgress = false,
  currentStep = 0,
  totalSteps = 1,
  children
}) => {
  const { t, simulationMode, showFeedback } = useNavigation();
  
  const handleBack = () => {
    if (onBack) {
      onBack();
    } else if (simulationMode) {
      showFeedback('success', `${t.simulatedNavigation} étape précédente du modal`);
    }
  };
  
  const handleNext = () => {
    if (onNext) {
      onNext();
    } else if (simulationMode) {
      showFeedback('success', `${t.simulatedNavigation} étape suivante du modal`);
    }
  };
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      
      {/* Modal */}
      <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
            <div className="flex items-center gap-2">
              {simulationMode && (
                <div className="flex items-center gap-1 px-2 py-1 bg-orange-100 rounded-full text-xs text-orange-800">
                  <WifiOff className="h-3 w-3" />
                  Preview
                </div>
              )}
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 p-1"
                aria-label="Fermer"
              >
                ✕
              </button>
            </div>
          </div>
          
          {/* Progress */}
          {showProgress && totalSteps > 1 && (
            <div className="mt-3">
              <div className="flex items-center justify-between text-sm text-gray-600 mb-1">
                <span>Étape {currentStep + 1} sur {totalSteps}</span>
                <span>{Math.round(((currentStep + 1) / totalSteps) * 100)}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-1">
                <div 
                  className="bg-blue-600 h-1 rounded-full transition-all duration-300"
                  style={{ width: `${((currentStep + 1) / totalSteps) * 100}%` }}
                />
              </div>
            </div>
          )}
        </div>
        
        {/* Content */}
        <div className="px-6 py-4 overflow-y-auto max-h-[60vh]">
          {children}
        </div>
        
        {/* Footer Navigation */}
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
          <div className="flex items-center justify-between">
            {(onBack || currentStep > 0) ? (
              <SmartBackButton 
                onCustomBack={handleBack}
                variant="minimal"
                disabled={isLoading}
              />
            ) : <div />}
            
            <div className="flex items-center gap-3">
              {onClose && (
                <button
                  onClick={onClose}
                  disabled={isLoading}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50"
                >
                  Annuler
                </button>
              )}
              
              {onNext && (
                <SmartNextButton
                  onCustomNext={handleNext}
                  label={nextLabel || (currentStep === totalSteps - 1 ? 'Terminer' : t.next)}
                  variant="primary"
                  disabled={nextDisabled}
                  isLoading={isLoading}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Keyboard Shortcuts Helper
export const KeyboardShortcuts = () => {
  const { goBack, goNext, t } = useNavigation();
  
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Alt + Left Arrow for Back
      if (e.altKey && e.key === 'ArrowLeft') {
        e.preventDefault();
        goBack();
      }
      
      // Alt + Right Arrow for Next
      if (e.altKey && e.key === 'ArrowRight') {
        e.preventDefault();
        goNext();
      }
      
      // Escape to go back
      if (e.key === 'Escape') {
        goBack();
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [goBack, goNext]);
  
  return null; // This is a behavior-only component
};

export default {
  GroupSearchNavigator,
  GroupCreationNavigator,
  JoinRequestNavigator,
  ScreenHeader,
  ModalNavigation,
  KeyboardShortcuts
};